import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtNZ } from "@/lib/datetime";
import { toast } from "sonner";
import { Megaphone, ThumbsUp, Check, CheckCheck, Eye, Trash2, Plus, Clock, X, Pin, AlertTriangle, Users, ChevronDown, Paperclip, Download, FileText, BellRing } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const fmtSize = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const isPdfAtt = (a) => (a.content_type || "").includes("pdf") || (a.filename || "").toLowerCase().endsWith(".pdf");

const AttachmentChip = ({ noticeId, att, canManage, onView, onDelete }) => {
  const [thumb, setThumb] = useState(null);
  const isImage = (att.content_type || "").startsWith("image/");
  const isPdf = isPdfAtt(att);
  useEffect(() => {
    let active = true;
    let objUrl;
    (async () => {
      try {
        if (isImage) {
          const r = await api.get(`/notices/${noticeId}/attachments/${att.id}`, { responseType: "blob" });
          objUrl = URL.createObjectURL(r.data);
          if (active) setThumb(objUrl);
          else URL.revokeObjectURL(objUrl);
        } else if (isPdf) {
          const r = await api.get(`/notices/${noticeId}/attachments/${att.id}`, { responseType: "arraybuffer" });
          const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(r.data) }).promise;
          const pg = await pdf.getPage(1);
          const base = pg.getViewport({ scale: 1 });
          const vp = pg.getViewport({ scale: 88 / base.width });
          const canvas = document.createElement("canvas");
          canvas.width = vp.width;
          canvas.height = vp.height;
          await pg.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
          const dataUrl = canvas.toDataURL("image/png");
          pdf.destroy();
          if (active) setThumb(dataUrl);
        }
      } catch {
        /* fall back to a generic icon */
      }
    })();
    return () => {
      active = false;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [noticeId, att.id, isImage, isPdf]);

  return (
    <div className="flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-lg p-1.5" data-testid={`notice-attachment-${att.id}`}>
      <button
        type="button"
        onClick={() => onView(att)}
        data-testid={`notice-attachment-thumb-${att.id}`}
        className="shrink-0 h-11 w-11 rounded-md border border-zinc-200 bg-white overflow-hidden flex items-center justify-center"
        title={att.filename}
      >
        {thumb ? (
          <img src={thumb} alt="" className="h-full w-full object-cover" />
        ) : isPdf ? (
          <FileText size={18} className="text-red-500" />
        ) : (
          <Paperclip size={16} className="text-zinc-400" />
        )}
      </button>
      <div className="min-w-0 flex-1">
        <button
          type="button"
          onClick={() => onView(att)}
          data-testid={`notice-attachment-view-${att.id}`}
          className="block w-full truncate text-left text-[11px] font-semibold text-brand hover:underline"
        >
          {att.filename}
        </button>
        <span className="text-[10px] text-zinc-400">{fmtSize(att.size || 0)}</span>
      </div>
      <button type="button" onClick={() => onView(att)} className="text-zinc-400 hover:text-brand shrink-0" title="Open" data-testid={`notice-attachment-open-${att.id}`}>
        <Download size={13} />
      </button>
      {canManage && (
        <button type="button" onClick={() => onDelete(att.id)} className="text-zinc-400 hover:text-red-600 shrink-0" title="Remove" data-testid={`notice-attachment-delete-${att.id}`}>
          <X size={13} />
        </button>
      )}
    </div>
  );
};

const AckPanel = ({ notice, onNudged }) => {
  const [open, setOpen] = useState(false);
  const [nudging, setNudging] = useState(false);
  const done = notice.ack_done || 0;
  const total = notice.ack_total || 0;
  const complete = total > 0 && done >= total;
  const waiting = notice.ack_waiting_names || [];
  const coolingDown =
    notice.last_nudged_at &&
    Date.now() - new Date(notice.last_nudged_at).getTime() < 60 * 60 * 1000;

  const nudge = async () => {
    setNudging(true);
    try {
      const r = await api.post(`/notices/${notice.id}/nudge`);
      onNudged?.(r.data.notice);
      toast.success(`Reminded ${r.data.nudged} ${r.data.nudged === 1 ? "person" : "people"}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not send reminder");
    } finally {
      setNudging(false);
    }
  };

  return (
    <div className="mt-2 border-t border-zinc-100 pt-2" data-testid={`notice-ack-panel-${notice.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          data-testid={`notice-ack-toggle-${notice.id}`}
          onClick={() => setOpen((o) => !o)}
          className={`inline-flex items-center gap-1 text-[11px] font-bold ${complete ? "text-emerald-600" : "text-amber-600"}`}
        >
          <Users size={11} /> {done} of {total} acknowledged
          <ChevronDown size={11} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {waiting.length > 0 && (
          <button
            type="button"
            data-testid={`notice-nudge-${notice.id}`}
            onClick={nudge}
            disabled={nudging || coolingDown}
            title={coolingDown ? "Already reminded recently — try again later" : `Remind ${waiting.length} still waiting`}
            className="inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2 py-0.5 bg-amber-500 text-white hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <BellRing size={11} /> {nudging ? "Sending…" : coolingDown ? "Reminded" : `Nudge ${waiting.length}`}
          </button>
        )}
      </div>
      {notice.last_nudged_at && (
        <div className="text-[10px] text-zinc-400 mt-1" data-testid={`notice-last-nudged-${notice.id}`}>
          Last reminded {fmtNZ(notice.last_nudged_at, { dateStyle: "medium", timeStyle: "short", tag: false })}
        </div>
      )}
      {open && (
        <div className="mt-2 space-y-2 text-[11px]" data-testid={`notice-ack-detail-${notice.id}`}>
          <div>
            <div className="font-bold text-emerald-700 mb-1">Acknowledged ({notice.ack_done_names.length})</div>
            {notice.ack_done_names.length ? (
              <div className="flex flex-wrap gap-1">
                {notice.ack_done_names.map((nm, i) => (
                  <span key={i} className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5">
                    <Check size={9} /> {nm}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-zinc-400">No one yet</span>
            )}
          </div>
          <div>
            <div className="font-bold text-amber-700 mb-1">Still waiting ({notice.ack_waiting_names.length})</div>
            {notice.ack_waiting_names.length ? (
              <div className="flex flex-wrap gap-1">
                {notice.ack_waiting_names.map((nm, i) => (
                  <span key={i} className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5">
                    <Clock size={9} /> {nm}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-emerald-600 font-semibold">Everyone's acknowledged 🎉</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const Noticeboard = () => {
  const { user } = useAuth();
  const canPost = user.role === "admin" || user.role === "manager";
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [mustAck, setMustAck] = useState(false);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const pickFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    setFiles((cur) => {
      const next = [...cur];
      for (const f of incoming) {
        if (next.length >= 2) {
          toast.error("A notice can have at most 2 attachments");
          break;
        }
        if (f.size > MAX_FILE_BYTES) {
          toast.error(`${f.name} is larger than 5 MB`);
          continue;
        }
        next.push(f);
      }
      return next;
    });
  };

  const load = () => {
    setLoading(true);
    api.get("/notices").then((r) => setNotices(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    setSaving(true);
    try {
      const r = await api.post("/notices", { title: title.trim(), body: body.trim(), must_ack: mustAck });
      const created = r.data;
      setNotices((ns) => [created, ...ns]);
      const staged = files;
      setTitle("");
      setBody("");
      setMustAck(false);
      setFiles([]);
      setComposing(false);
      toast.success(created.pending ? "Posted — awaiting admin approval" : "Notice posted");
      for (const f of staged) {
        try {
          const fd = new FormData();
          fd.append("file", f);
          const ur = await api.post(`/notices/${created.id}/attachments`, fd);
          setNotices((ns) => ns.map((n) => (n.id === created.id ? ur.data : n)));
        } catch (err) {
          toast.error(apiError(err.response?.data?.detail) || `Could not attach ${f.name}`);
        }
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not post notice");
    } finally {
      setSaving(false);
    }
  };

  const viewAttachment = async (noticeId, att) => {
    try {
      const r = await api.get(`/notices/${noticeId}/attachments/${att.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error("Could not open attachment");
    }
  };

  const deleteAttachment = async (noticeId, attId) => {
    try {
      const r = await api.delete(`/notices/${noticeId}/attachments/${attId}`);
      setNotices((ns) => ns.map((n) => (n.id === noticeId ? r.data : n)));
      toast.success("Attachment removed");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not remove attachment");
    }
  };

  const like = async (id) => {
    try {
      const r = await api.post(`/notices/${id}/like`);
      setNotices((ns) => ns.map((n) => (n.id === id ? r.data : n)));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not like");
    }
  };

  const markSeen = async (id) => {
    try {
      const r = await api.post(`/notices/${id}/seen`);
      setNotices((ns) => ns.map((n) => (n.id === id ? r.data : n)));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not mark as seen");
    }
  };

  const approve = async (id) => {
    try {
      const r = await api.post(`/notices/${id}/approve`);
      setNotices((ns) => ns.map((n) => (n.id === id ? r.data : n)));
      toast.success("Notice approved");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not approve");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/notices/${id}`);
      setNotices((ns) => ns.filter((n) => n.id !== id));
      toast.success("Notice deleted");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not delete");
    }
  };

  const togglePin = async (id) => {
    try {
      const r = await api.post(`/notices/${id}/pin`);
      // Reload to re-apply pinned-first ordering from the server.
      setNotices((ns) => ns.map((n) => (n.id === id ? r.data : n)));
      load();
      toast.success(r.data.pinned ? "Pinned to top" : "Unpinned");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not pin");
    }
  };

  return (
    <div
      className="w-80 shrink-0 flex flex-col rounded-xl border border-zinc-200 bg-white overflow-hidden"
      data-testid="noticeboard"
    >
      <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-200 bg-zinc-50">
        <Megaphone size={16} className="text-brand" />
        <h2 className="text-sm font-bold text-zinc-800 flex-1">Noticeboard</h2>
        {canPost && !composing && (
          <button
            type="button"
            data-testid="notice-new-button"
            onClick={() => setComposing(true)}
            className="flex items-center gap-1 text-xs font-bold text-brand hover:underline"
          >
            <Plus size={14} /> Post
          </button>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
        {canPost && composing && (
          <form onSubmit={submit} className="border border-zinc-200 rounded-lg p-3 bg-zinc-50/60 space-y-2" data-testid="notice-form">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-zinc-600 uppercase tracking-wider">New notice</span>
              <button type="button" onClick={() => setComposing(false)} className="text-zinc-400 hover:text-zinc-600">
                <X size={14} />
              </button>
            </div>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Title"
              maxLength={120}
              data-testid="notice-title-input"
              className="w-full text-sm border border-zinc-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand"
            />
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Message…"
              rows={3}
              maxLength={2000}
              data-testid="notice-body-input"
              className="w-full text-sm border border-zinc-300 rounded px-2 py-1.5 resize-none focus:outline-none focus:ring-2 focus:ring-brand"
            />
            {user.role === "manager" && (
              <p className="text-[11px] text-zinc-400">An administrator will review this before it's shown to everyone.</p>
            )}
            <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer select-none" data-testid="notice-mustack-label">
              <input
                type="checkbox"
                checked={mustAck}
                onChange={(e) => setMustAck(e.target.checked)}
                data-testid="notice-mustack-input"
                className="h-3.5 w-3.5 rounded border-zinc-300 text-brand focus:ring-brand"
              />
              <span className="inline-flex items-center gap-1">
                <AlertTriangle size={12} className="text-amber-500" />
                Require field staff to acknowledge
              </span>
            </label>
            <div className="space-y-1.5" data-testid="notice-attach-area">
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-[11px] bg-white border border-zinc-200 rounded px-2 py-1" data-testid={`notice-staged-file-${i}`}>
                  <Paperclip size={11} className="text-zinc-400 shrink-0" />
                  <span className="truncate flex-1">{f.name}</span>
                  <span className="text-zinc-400 whitespace-nowrap">{fmtSize(f.size)}</span>
                  <button type="button" onClick={() => setFiles((cur) => cur.filter((_, j) => j !== i))} className="text-zinc-400 hover:text-red-600" data-testid={`notice-staged-remove-${i}`}>
                    <X size={12} />
                  </button>
                </div>
              ))}
              {files.length < 2 && (
                <label className="flex items-center gap-1 text-[11px] font-semibold text-brand cursor-pointer hover:underline w-fit" data-testid="notice-attach-label">
                  <Paperclip size={12} /> Attach file (up to 2 · 5 MB each)
                  <input
                    type="file"
                    className="hidden"
                    data-testid="notice-attach-input"
                    onChange={(e) => {
                      pickFiles(e.target.files);
                      e.target.value = "";
                    }}
                  />
                </label>
              )}
            </div>
            <button
              type="submit"
              disabled={saving || !title.trim() || !body.trim()}
              data-testid="notice-submit-button"
              className="w-full bg-brand text-white rounded-full text-sm font-bold py-1.5 hover:bg-brand-dark transition-colors disabled:opacity-60"
            >
              {saving ? "Posting…" : "Post notice"}
            </button>
          </form>
        )}

        {loading ? (
          <div className="text-zinc-400 font-mono text-xs py-6 text-center">Loading…</div>
        ) : notices.length === 0 ? (
          <div className="text-center text-xs text-zinc-400 py-10 border border-dashed border-zinc-200 rounded-lg">
            No notices yet
          </div>
        ) : (
          notices.map((n) => (
            <div
              key={n.id}
              data-testid={`notice-${n.id}`}
              className={`border rounded-lg p-3 ${
                n.needs_ack
                  ? "border-red-400 bg-red-50 ring-1 ring-red-300"
                  : n.pending
                  ? "border-amber-300 bg-amber-50/60"
                  : n.pinned
                  ? "border-brand bg-brand-card-blue/30 ring-1 ring-brand/30"
                  : "border-zinc-200 bg-white"
              }`}
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {n.pinned && (
                      <span
                        data-testid={`notice-pinned-${n.id}`}
                        className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-brand bg-brand-card-blue border border-brand/40 rounded px-1 py-0.5"
                      >
                        <Pin size={9} /> Pinned
                      </span>
                    )}
                    {n.must_ack && (
                      <span
                        data-testid={`notice-mustack-badge-${n.id}`}
                        className={`inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider rounded px-1 py-0.5 border ${
                          n.needs_ack
                            ? "text-red-700 bg-red-100 border-red-300"
                            : "text-amber-700 bg-amber-100 border-amber-300"
                        }`}
                      >
                        <AlertTriangle size={9} /> {n.needs_ack ? "Action required" : "Must acknowledge"}
                      </span>
                    )}
                    <h3 className="font-bold text-sm text-zinc-900 leading-snug">{n.title}</h3>
                    {n.pending && (
                      <span
                        data-testid={`notice-pending-${n.id}`}
                        className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-300 rounded px-1 py-0.5"
                      >
                        <Clock size={9} /> Pending
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-zinc-600 mt-1 whitespace-pre-wrap break-words">{n.body}</p>
                  {n.attachments && n.attachments.length > 0 && (
                    <div className="mt-2 space-y-1.5" data-testid={`notice-attachments-${n.id}`}>
                      {n.attachments.map((att) => (
                        <AttachmentChip
                          key={att.id}
                          noticeId={n.id}
                          att={att}
                          canManage={n.can_manage_attachments}
                          onView={(a) => viewAttachment(n.id, a)}
                          onDelete={(aid) => deleteAttachment(n.id, aid)}
                        />
                      ))}
                    </div>
                  )}
                </div>
                {n.can_pin && (
                  <button
                    type="button"
                    data-testid={`notice-pin-${n.id}`}
                    onClick={() => togglePin(n.id)}
                    title={n.pinned ? "Unpin" : "Pin to top"}
                    className={`shrink-0 ${n.pinned ? "text-brand" : "text-zinc-300 hover:text-brand"}`}
                  >
                    <Pin size={14} className={n.pinned ? "fill-current" : ""} />
                  </button>
                )}
                {n.can_delete && (
                  <button
                    type="button"
                    data-testid={`notice-delete-${n.id}`}
                    onClick={() => remove(n.id)}
                    title="Delete notice"
                    className="text-zinc-300 hover:text-red-600 shrink-0"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2 mt-2.5 text-[11px] text-zinc-400">
                <span className="truncate">{n.author}</span>
                <span>·</span>
                <span className="whitespace-nowrap">{fmtNZ(n.created_at, { dateStyle: "medium", tag: false })}</span>
              </div>

              <div className="flex items-center gap-2 mt-2">
                {!n.pending && (
                  <button
                    type="button"
                    data-testid={`notice-like-${n.id}`}
                    onClick={() => like(n.id)}
                    className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 border transition-colors ${
                      n.liked
                        ? "bg-brand text-white border-brand"
                        : "bg-white text-zinc-600 border-zinc-300 hover:bg-zinc-50"
                    }`}
                  >
                    <ThumbsUp size={12} /> {n.like_count}
                  </button>
                )}
                {!n.pending && (
                  <button
                    type="button"
                    data-testid={`notice-seen-${n.id}`}
                    onClick={() => markSeen(n.id)}
                    disabled={n.seen}
                    title={
                      n.seen_names && n.seen_names.length
                        ? `Seen by: ${n.seen_names.join(", ")}`
                        : n.seen
                        ? "You've marked this seen"
                        : n.must_ack
                        ? "Tap to acknowledge"
                        : "Mark as seen"
                    }
                    className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 border transition-colors ${
                      n.seen
                        ? "bg-emerald-50 text-emerald-700 border-emerald-300 cursor-default"
                        : n.needs_ack
                        ? "bg-red-600 text-white border-red-600 hover:bg-red-700"
                        : "bg-white text-zinc-600 border-zinc-300 hover:bg-zinc-50"
                    }`}
                  >
                    {n.seen ? <CheckCheck size={12} /> : n.needs_ack ? <AlertTriangle size={12} /> : <Eye size={12} />}{" "}
                    {n.must_ack
                      ? n.seen
                        ? "Acknowledged"
                        : "Acknowledge"
                      : n.seen_count != null
                      ? `Seen ${n.seen_count}`
                      : "Seen"}
                  </button>
                )}
                {n.can_approve && (
                  <button
                    type="button"
                    data-testid={`notice-approve-${n.id}`}
                    onClick={() => approve(n.id)}
                    className="inline-flex items-center gap-1 text-xs font-bold rounded-full px-2.5 py-1 bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                  >
                    <Check size={12} /> Approve
                  </button>
                )}
              </div>
              {n.must_ack && n.ack_total !== undefined && (
                <AckPanel
                  notice={n}
                  onNudged={(upd) => setNotices((ns) => ns.map((x) => (x.id === upd.id ? upd : x)))}
                />
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
